import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * This set of utilities is used to provide a switch for focus styles.
 * Focus styles like outlines should only show up when the user is interacting with
 * the app using the keyboard. Once the mouse is used again, focus outlines should be
 * disabled.
 */

export var SHOW_FOCUS_MARKER = "lodestar--show-focus";
var getFocusModeFromBodyClassList = function getFocusModeFromBodyClassList() {
  return typeof document !== "undefined" && document.body.classList.contains(SHOW_FOCUS_MARKER) ? "keyboard" : "mouse";
};
/**
 * Hook that watches the document for key and mouse events to toggle between keyboard
 * and mouse modes.
 *
 * This hook assumes the initial mode to be mouse.
 */
export var useFocusMode = function useFocusMode() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    isDisabled = _ref.isDisabled,
    _ref$passive = _ref.passive,
    passive = _ref$passive === void 0 ? false : _ref$passive;
  var _useState = useState(getFocusModeFromBodyClassList),
    _useState2 = _slicedToArray(_useState, 2),
    focusMode = _useState2[0],
    setFocusMode = _useState2[1];
  var passiveFocusModeRef = useRef(focusMode);
  var getFocusModeFromPassiveRef = useCallback(function () {
    return passiveFocusModeRef.current;
  }, []);

  // Hooks up a MutationObserver that watches the body class. This means any change
  // to add/remove the class we care about will cause a re-render here.
  // This is good because many components could use this hook and we want them all
  // to pay attention.
  useEffect(function () {
    if (isDisabled) {
      return;
    }
    var handleUpdate = function handleUpdate() {
      var initialFocusMode = getFocusModeFromBodyClassList();
      passiveFocusModeRef.current = initialFocusMode;

      // only cause a React re-render if necessary.
      if (!passive) {
        setFocusMode(getFocusModeFromBodyClassList());
      }
    };

    // Do a quick update in case it has changed while we were disabled.
    handleUpdate();
    var obs = new MutationObserver(handleUpdate);
    obs.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"]
    });
    return function () {
      obs.disconnect();
    };
  }, [isDisabled, passive]);
  return {
    focusMode: focusMode,
    getFocusMode: getFocusModeFromPassiveRef,
    // This function needs to add/remove the marker class from the body. This allows the
    // mutation observer above to cause the re-render in ALL of the usages of these hooks,
    // not just this one.
    setFocusMode: useCallback(function (newMode) {
      switch (newMode) {
        case "keyboard":
          document.body.classList.add(SHOW_FOCUS_MARKER);
          break;
        case "mouse":
          document.body.classList.remove(SHOW_FOCUS_MARKER);
          break;
      }
    }, [])
  };
};
//# sourceMappingURL=useFocusMode.js.map